// The web client's keyed sync feed (docs/architecture/sync-system.md §7.3).
// Online-only, but it reads and writes the same protocol as the native apps:
// reads are incremental cursor pulls over GET /sync (never a full refetch),
// writes are field-level ops over POST /sync applied optimistically and
// reverted if the server says no. Merge results (`merged`/`deleted`) adopt the
// server's authoritative record immediately.
//
// One feed serves every entity type: the cursor, the HLC clock, and the pull
// loop live here, and each incoming record is fanned out to the collection that
// registered for its entityType. Giving each entity its own feed would make each
// one re-pull the whole compacted feed from 0.

import type { EntityType, SyncOp, SyncPushResponse, SyncRecord } from "@/types";
import type { Assistant8Client } from "@/api/client";
import { EntityCollection } from "./entityCollection";
import { HlcClock, deviceId } from "./hlc";

const ERR_REJECTED = "The change was rejected by the server.";
const ERR_BUSY = "The server is busy; try again.";

/** What the feed needs from a collection to drive it. */
export interface RecordSink {
  readonly entityType: EntityType;
  apply(record: SyncRecord): void;
  adoptVersion(id: string, version: number, seq?: number): void;
  adoptRecord(record: SyncRecord): void;
  reset(): void;
  emit(): void;
}

export class SyncFeed {
  private readonly sinks = new Map<EntityType, RecordSink>();
  private readonly clock = new HlcClock();
  // Session-scoped, NOT persisted: collections keep records only in memory, so
  // every fresh page load must re-pull the whole compacted feed from 0 to
  // rebuild state. Persisting the cursor without the records would advance past
  // every existing record and show an empty list after a refresh.
  private cursorSeq = 0;
  private pulling = false;

  constructor(private readonly api: Assistant8Client) {}

  /** Create a collection and register it for its entity type in one step, so a
   * collection can never exist unregistered (and silently miss the feed). */
  collectionFor<TFields extends object>(
    entityType: EntityType,
    fieldNames: readonly (keyof TFields & string)[],
  ): EntityCollection<TFields> {
    const collection = new EntityCollection<TFields>(entityType, fieldNames, this);
    this.sinks.set(entityType, collection);
    return collection;
  }

  /** A monotonic HLC stamp for a field write. */
  stamp(): string {
    return this.clock.now();
  }

  /** Initial load: catch up from the current cursor. */
  async start(): Promise<void> {
    await this.pull();
  }

  /** A nudge with the latest feed position: pull only when actually behind. */
  async pullIfBehind(latestSeq?: number): Promise<void> {
    if (latestSeq !== undefined && latestSeq <= this.cursorSeq) return;
    await this.pull();
  }

  /** Drop all local state (sign-out) across every entity type. */
  reset(): void {
    this.cursorSeq = 0;
    for (const sink of this.sinks.values()) sink.reset();
  }

  private async pull(): Promise<void> {
    if (this.pulling) return;
    this.pulling = true;
    try {
      for (;;) {
        const page = await this.api.pullSync(String(this.cursorSeq));
        for (const record of page.changes) this.dispatch(record);
        this.cursorSeq = Number(page.cursor) || this.cursorSeq;
        this.emitAll();
        if (!page.hasMore) break;
      }
    } finally {
      this.pulling = false;
    }
  }

  /** Route one record to its collection. Records for entity types nothing is
   * registered for (the feed carries all of them) are ignored. */
  private dispatch(record: SyncRecord): void {
    for (const hlc of Object.values(record.clocks)) this.clock.observe(hlc);
    this.sinks.get(record.entityType)?.apply(record);
  }

  // --- Push plumbing ---

  /** Send one op; adopt the result or roll back and rethrow. */
  async push(op: SyncOp, revert: () => void): Promise<void> {
    const sink = this.sinks.get(op.entityType);
    let response: SyncPushResponse;
    try {
      response = await this.api.pushSync({
        deviceId: deviceId(),
        cursor: String(this.cursorSeq),
        ops: [op],
      });
    } catch (error) {
      // Transport failure (offline, 5xx): the web client has no durable
      // outbox — undo the optimistic change and let the UI say so.
      revert();
      sink?.emit();
      throw error;
    }

    this.clock.updateServerOffset(response.serverTime);
    try {
      this.adopt(op, response, revert);
    } finally {
      sink?.emit();
    }

    if (response.latestSeq !== undefined && response.latestSeq > this.cursorSeq) {
      await this.pull();
    }
  }

  private adopt(op: SyncOp, response: SyncPushResponse, revert: () => void): void {
    const sink = this.sinks.get(op.entityType);
    const result = response.results.find((r) => r.opId === op.opId);
    switch (result?.status) {
      case "applied":
        if (result.version !== undefined) sink?.adoptVersion(op.id, result.version, result.seq);
        return;
      case "merged":
      case "deleted":
        // Concurrent changes existed — the server's record is authoritative.
        if (result.record) this.sinks.get(result.record.entityType)?.adoptRecord(result.record);
        return;
      case "noop":
        return;
      case "retryLater":
        revert();
        throw new Error(ERR_BUSY);
      default:
        revert();
        throw new Error(result?.message ?? ERR_REJECTED);
    }
  }

  private emitAll(): void {
    for (const sink of this.sinks.values()) sink.emit();
  }
}
